import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import DnsIcon from "@mui/icons-material/Dns";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ResultCard from "./ResultCard";
import { ResultData } from "./types";
import { updateDNS, validateDNS, addCustomTenantDomain } from "../../apis/postProvisionApi/postProvisionApi";

type DNSFormData = {
  subdomainPrefix: string;
  subscriptionId: string;
  resourceGroupName: string;
}; 

type DNSBindingProps = {
  clientId: any;
  clientName: string;
  onComplete?: () => void;
};

export default function DNSBinding({ clientId, clientName, onComplete }: DNSBindingProps) {
  const location = useLocation();
  const isConfigurePath = location.pathname.includes("/tenants/configure/");

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    watch,
  } = useForm<DNSFormData>({
    defaultValues: {
      subdomainPrefix: "",
      subscriptionId: "",
      resourceGroupName: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [addingCustomDomain, setAddingCustomDomain] = useState(false);
  const [results, setResults] = useState<ResultData | null>(null);
  const [validationResults, setValidationResults] = useState<ResultData | null>(null);
  const [error, setError] = useState("");
  const [expandedResults, setExpandedResults] = useState<{ [key: string]: boolean }>({});

  const onUpdateDNS = async (formData: DNSFormData) => {
    if (!clientId || !clientName.trim()) {
      setError("Client ID and Client Name are required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await updateDNS({
        subdomainPrefix: formData.subdomainPrefix,
        clientId,
        clientName,
      });
      setResults(data);

      if (!data.success) {
        setError(data.error || "Operation failed");
      } else if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      setError(`Failed to update DNS records: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onValidateDNS = async (formData: DNSFormData) => {
    setValidating(true);
    setError("");

    try {
      const data = await validateDNS({ subdomainPrefix: formData.subdomainPrefix });
      setValidationResults(data);

      if (!data.success) {
        setError(data.error || "Validation failed");
      }
    } catch (err: any) {
      setError(`Failed to validate DNS records: ${err.message}`);
    } finally {
      setValidating(false);
    }
  };

  const onAddCustomDomain = async (formData: DNSFormData) => {
    if (!clientId || !clientName.trim()) {
      setError("Client ID and Client Name are required");
      return;
    }

    setAddingCustomDomain(true);
    setError("");

    try {
      const data = await addCustomTenantDomain({
        subdomainPrefix: formData.subdomainPrefix,
        tenantId: clientId,
        subscriptionId: formData.subscriptionId,
        resourceGroupName: formData.resourceGroupName,
        tenantName: clientName,
      });

      setResults(data);

      if (!data.success) {
        setError(data.error || "Operation failed");
      } else if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      setError(`Failed to add custom tenant domain: ${err.message}`);
    } finally {
      setAddingCustomDomain(false);
    }
  };

  const toggleExpanded = (resultType: string, index: number) => {
    const key = `${resultType}-${index}`;
    setExpandedResults((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <DnsIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Typography variant="h4" component="h1">
          DNS Mapping Management
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Configuring DNS Records for p360connect.biz
          </Typography>

          <TextField
            fullWidth
            label="Subdomain Prefix"
            placeholder="staging-"
            sx={{ mb: 2 }}
            {...register("subdomainPrefix", {
              required: "Subdomain prefix is required",
            })}
            error={!!errors.subdomainPrefix}
            helperText={errors.subdomainPrefix?.message}
          />

          {isConfigurePath && (
            <>
              <TextField
                fullWidth
                label="Subscription ID"
                placeholder="Enter Azure Subscription ID"
                sx={{ mb: 2 }}
                {...register("subscriptionId", {
                  required: "Subscription ID is required for custom domain",
                })}
                error={!!errors.subscriptionId}
                helperText={errors.subscriptionId?.message}
              />

              <TextField
                fullWidth
                label="Resource Group Name"
                placeholder="Enter Azure Resource Group Name"
                sx={{ mb: 3 }}
                {...register("resourceGroupName", {
                  required: "Resource Group Name is required for custom domain",
                })}
                error={!!errors.resourceGroupName}
                helperText={errors.resourceGroupName?.message}
              />
            </>
          )}

          <Typography variant="body2">
            <strong>Client ID:</strong> {clientId}
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
            <strong>Client Name:</strong> {clientName}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            {isConfigurePath ? (
              // Only show Add Custom Tenant Domain button for /tenants/configure/ path
              <Button
                variant="contained"
                color="secondary"
                onClick={handleFormSubmit(onAddCustomDomain)}
                disabled={addingCustomDomain}
                startIcon={addingCustomDomain ? <CircularProgress size={20} /> : <SendIcon />}
                size="large"
              >
                {addingCustomDomain ? "Adding Custom Domain..." : "Add Custom Tenant Domain"}
              </Button>
            ) : (
              // Show Update DNS and Validate buttons for other paths
              <>
                <Button
                  variant="contained"
                  onClick={handleFormSubmit(onUpdateDNS)}
                  disabled={loading || validating}
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  size="large"
                >
                  {loading ? "Updating DNS..." : "Update DNS Records"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleFormSubmit(onValidateDNS)}
                  disabled={loading || validating}
                  startIcon={validating ? <CircularProgress size={20} /> : <VisibilityIcon />}
                  size="large"
                >
                  {validating ? "Validating..." : "Validate DNS Records"}
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {results && (
        <ResultCard
          title="DNS Update Results"
          results={results}
          resultType="update"
          expandedResults={expandedResults}
          onToggle={toggleExpanded}
        />
      )}

      {validationResults && (
        <ResultCard
          title="DNS Validation Results"
          results={validationResults}
          resultType="validation"
          expandedResults={expandedResults}
          onToggle={toggleExpanded}
        />
      )}
    </Container>
  );
}
